import { useMemo } from "react";
import { useForm } from "react-hook-form";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { useSubmitAttemptMutation } from "@/redux/service";
import { DisplayType } from "@/redux/datablocks/types";
import BaseButton from "@/components_v2/Button/BaseButton";
import { BUTTON_VARIANTS } from "@/components_v2/Button/constants";
import {
  HookFormInput,
  HookFormTextArea,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseProgressBar from "@/components_v2/ProgressBars/BaseProgressBar";
import { KnowledgeCheckModalProps } from "@/features/certifications/module/components/types";
import { ANSWER_VERDICTS } from "@/domains/certifications/constants";

import "./styles.css";

const MODAL_WIDTH = 1000;
const BAR_STROKE_WIDTH = 4;
const TEXT_AREA_ROWS = { minRows: 3, maxRows: 10 };

const fieldName = (fieldId: number) => `question${fieldId}`;

const KnowledgeCheckModal = ({
  open,
  certificationSlug,
  weekPosition,
  check,
  previousAttempt,
  onClose,
}: KnowledgeCheckModalProps) => {
  const { t } = useTranslation();
  const [submitAttempt, { isLoading, isError, reset }] =
    useSubmitAttemptMutation();

  const questions = useMemo(
    () => [...check.questions.templateFields].sort((a, b) => a.order - b.order),
    [check],
  );
  const schema = useMemo(
    () =>
      yup.object(
        Object.fromEntries(
          questions.map((question) => [
            fieldName(question.id),
            yup
              .string()
              .trim()
              .required(t("certification.module.check.modal.required")),
          ]),
        ),
      ),
    [questions, t],
  );
  const feedbackByField = useMemo(
    () =>
      new Map(
        (previousAttempt?.answers ?? [])
          .filter((answer) => answer.verdict === ANSWER_VERDICTS.INCORRECT)
          .map((answer) => [answer.field, answer.feedback]),
      ),
    [previousAttempt],
  );

  const form = useForm<Record<string, string>>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: Object.fromEntries(
      questions.map((question) => [fieldName(question.id), ""]),
    ),
  });
  const values = form.watch();
  const answeredCount = questions.filter(
    (question) => (values[fieldName(question.id)] ?? "").trim().length > 0,
  ).length;

  const handleClose = () => {
    if (isLoading) {
      return;
    }
    reset();
    form.reset();
    onClose();
  };

  const submit = form.handleSubmit((answers) =>
    submitAttempt({
      certificationSlug,
      knowledgeCheckId: check.id,
      answers: questions.map((question) => ({
        field: question.id,
        value: (answers[fieldName(question.id)] ?? "").trim(),
      })),
    })
      .unwrap()
      .then(handleClose)
      .catch((error: FetchBaseQueryError) => {
        if (error.status === 409) {
          handleClose();
        }
      }),
  );
  const isSubmitting = isLoading || form.formState.isSubmitting;
  const handleSubmit = () => {
    if (!isSubmitting) {
      void submit();
    }
  };

  return (
    <BaseModal
      open={open}
      width={MODAL_WIDTH}
      onCancel={handleClose}
      closable={!isLoading}
      maskClosable={false}
      keyboard={!isLoading}
      title={t("certification.module.check.modal.title", {
        position: weekPosition,
      })}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-300">
          <div className="module-check-count flex flex-col gap-100">
            <BaseProgressBar
              value={answeredCount}
              total={questions.length || 1}
              strokeWidth={BAR_STROKE_WIDTH}
              className="module-progress-bar full-width"
              label={
                <span className="certification-muted body-xs-regular">
                  {t("certification.module.check.modal.answered", {
                    current: answeredCount,
                    total: questions.length,
                  })}
                </span>
              }
            />
          </div>

          <div className="flex flex-wrap items-center justify-end gap-200">
            {isError && (
              <span className="module-error-text body-xs-regular" role="alert">
                {t("certification.module.check.modal.error")}
              </span>
            )}

            <BaseButton
              variant={BUTTON_VARIANTS.TERTIARY}
              onClick={handleClose}
              disabled={isLoading}
            >
              {t("buttons.cancel")}
            </BaseButton>

            <BaseButton
              onClick={handleSubmit}
              disabled={answeredCount < questions.length}
              loading={isSubmitting}
            >
              {t("certification.module.check.modal.submit")}
            </BaseButton>
          </div>
        </div>
      }
    >
      <form
        className="flex flex-col gap-400"
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        {questions.map((question, index) => {
          const feedback = feedbackByField.get(question.id);
          const name = fieldName(question.id);
          const feedbackId = `${name}-feedback`;
          const placeholder =
            question.placeholder ||
            t("certification.module.check.modal.placeholder");
          const describedBy = feedback !== undefined ? feedbackId : undefined;

          return (
            <div key={question.id} className="flex flex-col gap-200">
              <label
                htmlFor={name}
                className="certification-heading body-s-medium"
              >
                {t("certification.module.check.modal.question", {
                  number: index + 1,
                  title: question.title,
                })}
              </label>

              {feedback !== undefined && (
                <div
                  id={feedbackId}
                  className="module-answer-feedback flex flex-col items-start gap-200"
                >
                  <span className="module-status-tag module-status-tag-warning body-xs-regular">
                    {t("certification.module.check.modal.needsAnotherAnswer")}
                  </span>

                  {feedback && (
                    <p className="module-body-text body-s-regular">
                      {feedback}
                    </p>
                  )}
                </div>
              )}

              {question.displayType === DisplayType.INPUT ? (
                <HookFormInput
                  id={name}
                  name={name}
                  control={form.control}
                  placeholder={placeholder}
                  aria-describedby={describedBy}
                />
              ) : (
                <HookFormTextArea
                  id={name}
                  name={name}
                  control={form.control}
                  autoSize={TEXT_AREA_ROWS}
                  placeholder={placeholder}
                  aria-describedby={describedBy}
                />
              )}
            </div>
          );
        })}
      </form>
    </BaseModal>
  );
};

export default KnowledgeCheckModal;
